import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { AlertDetails } from '../../../API/types/Alerts';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import useNoThumbnail from '../../../theme/useNoThumbnail';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import Tags from '../../../components/common/Tags';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import EntityLink from '../../../components/ui/EntityLink/EntityLink';
import { getFormattedDateWithComma, getTime, getTimeZone } from '../../../utils/dateUtils';
import { alertEventsMap } from '../constants/alertEventsMap';
import { alertSensorMap } from '../constants/alertSensorMap';
import { getAlertThumbnail, getAlertDriverPhotoUrl } from '../helpers';
import { buildAlertDetailsLocationState } from '../../../utils/alertNavigation';
import S from './AlertsItemStyles';
import { ThemeType } from '../../../theme/themeType';
import VideoPreview from './VideoPreview/VideoPreview';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import alertFocusStore, { FocusedAlert } from '../../../store/AlertFocusStore';
import { ListNavigationState } from '../../../utils/listNavigation';

const getAlertCamera = (alert?: AlertDetails): number | null => {
  if (alert?.sensor === 'inwardCamera' || alert?.sensor === 'cloud') {
    if (alert?.thumbnailInwardCameraUrl) {
      return 2;
    }
    if (alert?.thumbnailFrontCameraUrl) {
      return 1;
    }
    return null;
  }
  if (alert?.thumbnailFrontCameraUrl) {
    return 1;
  }
  if (alert?.thumbnailInwardCameraUrl) {
    return 2;
  }
  return null;
};

type Props = {
  item?: AlertDetails;
  lightVersion?: boolean;
  showVehicleCard?: boolean;
  includeVehicleDetails?: boolean;
  hideEntityDetails?: boolean;
  listNavigation?: ListNavigationState;
};

function AlertsItem({
  item, lightVersion, showVehicleCard, includeVehicleDetails, hideEntityDetails, listNavigation,
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const noThumbnail = useNoThumbnail();
  const navigate = useNavigate();
  const location = useLocation();
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const {
    _id: id,
    name,
    riskLevel,
    sensor,
    eventDate,
    driver,
    driverId,
    vehicle,
    vehicleId,
    timeSynchronization,
  } = item!;

  const { shortDesc, fullDesc, badge } = alertEventsMap[name as string];
  // Lets a map showing a route history single out this alert while the row is hovered,
  // instead of drawing every alert of the period over the path.
  const focusableAlert: FocusedAlert | undefined = (item?.lat && item?.lon) ? {
    _id: id, lat: item.lat, lon: item.lon, name, riskLevel,
  } : undefined;
  // Depending on the endpoint, an embedded entity carries its detail-page id as `_id` or as the
  // `vehicleId` / `driverId` alias, so fall back through both.
  const vehicleDetailsId = vehicleId || vehicle?._id || vehicle?.vehicleId;
  const driverDetailsId = driverId || driver?._id || driver?.driverId;
  const vehicleLink = vehicleDetailsId ? `/vehicles/${vehicleDetailsId}` : undefined;
  const alertThumbnail = getAlertThumbnail(item);
  const alertCamera = getAlertCamera(item);
  const isVideoThumbnail = !!alertCamera && !getAlertDriverPhotoUrl(item);

  return (
    <S.Wrapper
      onMouseEnter={focusableAlert ? () => alertFocusStore.setHovered(focusableAlert) : undefined}
      onMouseLeave={focusableAlert ? () => alertFocusStore.clearHovered(id) : undefined}
    >
      {!!item && (
        <ListItemWrapper
          onClick={() => navigate(`/alerts/${id}`, {
            state: {
              ...buildAlertDetailsLocationState(
                location.pathname,
                location.search,
                location.state,
              ),
              listNavigation,
            },
          })}
          alertItem={lightVersion}
          data-list-item-id={id}
        >
          <div className={`risk-level ${riskLevel}`} />
          {showVideoPreview && isVideoThumbnail && (
            <VideoPreview alertId={id} cameraId={alertCamera || 1} />
          )}
          <Tags.ItemOverview
            avatar={alertThumbnail || noThumbnail}
            avatarAlt="alert"
            title={shortDesc}
            subtitle={fullDesc}
            isVideoThumbnail={isVideoThumbnail}
            onMouseOver={() => (isVideoThumbnail && setShowVideoPreview(true))}
            onMouseOut={() => (isVideoThumbnail && setShowVideoPreview(false))}
          />
          {!item.isValid && (
            <Tags.Status
              label="Invalid alert"
              backgroundColor={theme.colors.badge_invalid}
            />
          )}
          <Tags.Toggler
            type={alertSensorMap[sensor]}
            trigger={badge}
          />
          {!hideEntityDetails && (
          <div className="tags-container">
            {showVehicleCard ? (
              <>
                {!!vehicle && (
                  <EntityLink
                    to={vehicleLink}
                    label={`Open vehicle ${vehicle.licensePlateNumber}`}
                  >
                    <Tags.Vehicle
                      imageSrc={vehicleThumbnail}
                      licensePlateNumber={vehicle.licensePlateNumber ?? ''}
                      model={`${vehicle.manufacturer ? `${vehicle.manufacturer} ` : ''}${vehicle.model || ''}`}
                    />
                  </EntityLink>
                )}
                {!vehicle && (
                  <div className="no-assigned-driver">No assigned vehicle</div>
                )}
              </>
            ) : (
              <>
                {!!driver && (
                  <EntityLink
                    to={driverDetailsId ? `/drivers/${driverDetailsId}` : undefined}
                    label={`Open driver ${driver.firstName} ${driver.lastName}`}
                  >
                    <Tags.Driver
                      imageSrc={driver.avatarUrl || driverThumbnail}
                      name={`${driver.firstName} ${driver.lastName}`}
                      licenseName={driver.licenseName || ''}
                    />
                  </EntityLink>
                )}
                {includeVehicleDetails && !!vehicle && (
                  <EntityLink
                    to={vehicleLink}
                    label={`Open vehicle ${vehicle.licensePlateNumber}`}
                  >
                    <Tags.Vehicle
                      imageSrc={vehicleThumbnail}
                      licensePlateNumber={vehicle.licensePlateNumber ?? ''}
                      model={`${vehicle.manufacturer ? `${vehicle.manufacturer} ` : ''}${vehicle.model || ''}`}
                    />
                  </EntityLink>
                )}
                {!driver && !includeVehicleDetails && (
                  <div className="no-assigned-driver">No assigned driver</div>
                )}
                {includeVehicleDetails && !vehicle && (
                  <div className="no-assigned-driver">No assigned vehicle</div>
                )}
              </>
            )}
          </div>
          )}
          <Tags.Status
            label={timeSynchronization === 'ALL_SYNCHRONIZED' ? 'Synchronized' : 'Not Synchronized'}
            backgroundColor={timeSynchronization === 'ALL_SYNCHRONIZED' ? theme.colors.green : theme.colors.red}
          />
          {/* <Tags.Status
            label={timeSynchronization === 'ALL_SYNCHRONIZED' ? 'Alert Time Synchronized' : 'Alert Time Not Synchronized'}
            backgroundColor={timeSynchronization === 'ALL_SYNCHRONIZED' ? theme.colors.green : theme.colors.red}
          /> */}
          <Tags.LabeledIcon
            label={`${getTime(eventDate)} (${getTimeZone(eventDate)})`}
            icon={IconKeys.TIME}
            flexible
          />
          <Tags.LabeledIcon
            label={getFormattedDateWithComma(item.eventDate)}
            icon={IconKeys.DATE}
          />
          {!lightVersion && <Tags.Icon icon={IconKeys.LOCATION} className="map-icon" />}
          {!lightVersion && <Tags.ProceedToDetails />}
        </ListItemWrapper>
      )}
    </S.Wrapper>
  );
}

export default AlertsItem;
